import { useEffect, useRef } from 'react';
import { SECTION_IDS, type SectionId, type SectionReport } from '../../core/types';
import { useActiveSection } from '../../hooks/useActiveSection';
import { cn } from '../../lib/cn';
import { scoreTone, sectionHue } from '../../lib/tone';

export interface SectionNavProps {
  sections: readonly SectionReport[];
  /** Scrolls the matching form section into view and focuses its first field. */
  onJump: (section: SectionId) => void;
}

/**
 * Stable across renders on purpose.
 *
 * This array is an effect dependency inside `useActiveSection`, so building a
 * fresh one per render would tear down and rebuild the `IntersectionObserver` on
 * every keystroke. Derived from `SECTION_IDS` rather than from `report.sections`
 * for the same reason: the report object is new on every analysis.
 */
const ANCHOR_IDS = SECTION_IDS.map((id) => `section-${id}`);

/**
 * The second header bar: the five sections, with their live scores.
 *
 * The problem it solves is measured, not aesthetic. The analyser page is around
 * 9000px tall on a phone and the only content link in the chrome was "How it
 * works", which is itself hidden below 640px — so there was no way to get from
 * the Skills section back to the Headline except scrolling and hoping. This is
 * both the map and the progress bar: where the sections are, which one you are
 * in, and how each of them is currently scoring.
 *
 * Design notes worth keeping:
 *
 *  - **The scores are the point.** A strip of five plain labels is wayfinding; a
 *    strip of five labels with `scoreTone()` numbers beside them also tells you
 *    where the problem is, which is the question people actually open this tool
 *    to answer.
 *  - **The pills are links, not buttons.** In-page navigation is what `href`
 *    means, and it keeps the whole strip out of the way of `getByRole('button')`.
 *    The click handler is an upgrade on top: it moves focus into the section as
 *    well as the viewport, so a keyboard user's next Tab lands where they asked
 *    to be, exactly like the score rows in the report.
 *  - **`aria-current="location"`** rather than `"page"`. These are positions
 *    within one document, which is precisely what ARIA reserves `location` for.
 *  - **Labels are overridden with `aria-label`.** Visually a pill reads
 *    "Headline 68"; a screen reader would otherwise be handed the bare digit with
 *    no scale. The wording deliberately avoids "Jump to the … section", which is
 *    the report rows' phrasing — two controls with the same accessible name would
 *    be ambiguous to a voice-control user, and there is a test that says so.
 */
export function SectionNav({ sections, onJump }: SectionNavProps) {
  const active = useActiveSection(ANCHOR_IDS);
  const strip = useRef<HTMLDivElement | null>(null);
  const shown = useRef<string | null>(null);

  // Keep the current pill in view as the page scrolls past it. Without this the
  // strip is worse than useless on a phone: it says "you are in Skills" while
  // Skills is scrolled off the right-hand edge.
  useEffect(() => {
    const container = strip.current;
    const previous = shown.current;
    shown.current = active;
    // Nothing to do on the first pass — the strip already starts scrolled to the
    // left, which is where the first section is.
    if (!container || !active || previous === null || previous === active) return;
    const pill = container.querySelector<HTMLElement>(`[data-anchor="${active}"]`);
    if (!pill || typeof container.scrollTo !== 'function') return;
    // The strip's own scroll offset, deliberately not `scrollIntoView`: that can
    // scroll the page as well as the strip, and the strip is *driven* by page
    // scroll — which is a feedback loop, not a nicety.
    container.scrollTo({
      left: Math.max(0, pill.offsetLeft - (container.clientWidth - pill.offsetWidth) / 2),
      behavior: 'smooth',
    });
  }, [active]);

  return (
    <nav aria-label="Profile sections" className="border-t border-line">
      {/* `px-1`, not the `px-4` of the bar above, and the arithmetic is the
          point: a pill carries its own `px-3`, so 4px of container padding puts
          the *label* at 16px — the same left edge as the wordmark. Matching the
          bar's padding instead would align the pill's tint box and push its text
          8px inward, which makes the header's left edge jump every time the
          active section changes. Letting the tint bleed 12px past the content
          column is also the right affordance for a horizontal scroller: content
          running to the edge is how you know there is more of it. */}
      <div
        ref={strip}
        className="no-scrollbar mx-auto flex h-11 max-w-6xl items-center gap-1 overflow-x-auto px-1"
      >
        {sections.map((section) => {
          const anchor = `section-${section.section}`;
          const current = anchor === active;
          const scored = section.score !== null;
          const score = section.score ?? 0;
          const tone = scoreTone(score);

          return (
            <a
              key={section.section}
              href={`#${anchor}`}
              data-anchor={anchor}
              aria-current={current ? 'location' : undefined}
              aria-label={
                scored
                  ? `${section.label}, scoring ${score} of 100`
                  : `${section.label}, nothing to score yet`
              }
              onClick={(event) => {
                event.preventDefault();
                onJump(section.section);
              }}
              className={cn(
                'flex shrink-0 items-center gap-1.5 rounded-pill px-3 py-1.5 text-sm whitespace-nowrap no-underline transition-colors duration-200',
                current
                  ? cn('font-semibold', sectionHue(section.section))
                  : 'font-medium text-muted hover:bg-sunken hover:text-strong'
              )}
            >
              {section.label}
              <span
                className={cn('text-xs font-bold tabular-nums', scored ? tone.text : 'text-subtle')}
              >
                {scored ? score : '—'}
              </span>
            </a>
          );
        })}
      </div>
    </nav>
  );
}
